import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector} from "react-redux";
import Drawer from "../../components/Drawer/Drawer";
import Card from "../../components/Card/Card";
import { PiBellRinging } from "react-icons/pi";
import { UsersService } from "../../services";
import { FaCircle } from "react-icons/fa";
import Button from "../../components/Button/Button";
import Avatar from "../../components/Avatar/Avatar";
import Flex from "../../components/Flex/Flex";
import { FaSpinner } from "react-icons/fa";
import { showToast } from "../../redux/toastSlice";
import {formatTimeAgo} from "../../utils/formatTimeAgo";

const Navbar = () => {
    const navigate = useNavigate();
    const logo = useSelector((state) => state.projectInfo.logo);
    const currentUserId = useSelector((state) => state.projectInfo.uid);
    const users = useSelector(state => state.users.entities) || {};
    const user = users[currentUserId] || {};
    const userImage = user.user_image;
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [notificationItemLoading, setNotificationItemLoading] = useState(undefined);
    const dispatch = useDispatch();
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const notificationsListRef = useRef(null);

    const truncateText = (text, maxLength = 100) => {
        if (!text) return "";
        let truncated = text.length > maxLength ? text.slice(0, maxLength) + "..." : text;
        // Replace [@Name](/community/profiles/ID) with a badge-like span
        return truncated.replace(/\[@([^\]]+)\]\(\/community\/profiles\/\d+\)/g, '<span style="background:#d8ecff;color:#000000;padding:4px 8px;border-radius:6px;font-weight:500;">@$1</span style="background:#91caff;color:#3730a3;padding:4px 8px;border-radius:6px;font-weight:500;">');
    };

    // Fetch notifications with pagination
    const fetchNotifications = async (pageNum = 1, append = false) => {
        if (loadingMore || loading) return;
        if (!hasMore && append) return;
        if (append) setLoadingMore(true);
        else setLoading(true);
        try {
            const res = await UsersService.getUserNotifications(pageNum, 20);
            if (res.status === 200 && res.data.success) {
                const newNotifications = res.data.data;
                setNotifications(prev =>
                    append ? [...prev, ...newNotifications] : newNotifications
                );
                setHasMore(newNotifications.length === 20);
            } else {
                dispatch(showToast({ message: "Failed to fetch notifications.", type: "error" }));
            }
        } catch (err) {
            dispatch(showToast({ message: "Failed to fetch notifications.", type: "error" }));
        } finally {
            if (append) setLoadingMore(false);
            else setLoading(false);
        }
    };

    useEffect(() => {
        fetchNotifications(1, false).then(res=>{
            console.log(res);
        }).catch(err=>{
            console.error(err)
        })
    }, []);

    // Scroll handler for infinite scroll
    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        if (scrollHeight - scrollTop - clientHeight < 40 && hasMore && !loadingMore) {
            const nextPage = page + 1;
            fetchNotifications(nextPage, true).catch(err=>{
                console.error(err)
            })
            setPage(nextPage);
        }
    };

    const handleProfileClick = () => {
        navigate(`/community/profiles/${currentUserId}`);
    };

    /**
     * Handle notification click - mark as read and navigate if link exists
     * @param notif
     */
    const handleNotificationClick = (notif) => {
        handleMarkAsRead(notif.id).then(r => {}).catch(err=>{
            dispatch(showToast({message: "Error marking notification as read on click", type: "error"}));
            console.error("Error marking notification as read on click:", err);
        })
        if (notif.link) {
            navigate(notif.link);
            setDrawerOpen(false);
        }
    }


    /**
     * Handle marking notification(s) as read - single or all
     * @param notifId
     * @param mark_all
     * @returns {Promise<void>}
     */
    const handleMarkAsRead = async (notifId,mark_all=false) => {
        setNotificationItemLoading(notifId);
        UsersService.markNotificationAsRead(notifId,mark_all).then(res=>{
            if (res.status === 200 || res.data.success) {
                // mark all notifications as read when mark_all is true
                if(mark_all===true){
                    setNotifications(notifications =>
                        notifications.map(n =>
                            ({ ...n, is_read: 1 })
                        )
                    );
                    return;
                }
                setNotifications(notifications =>
                    notifications.map(n =>
                        n.id === notifId ? { ...n, is_read: 1 } : n
                    )
                );
            }
        }).catch(err=>{
            console.error("Error marking notification as read:", err);
        }).finally(()=>{
            setNotificationItemLoading(undefined);
        })
    };

    const unreadCount = notifications?.filter(n => n?.is_read === 0)?.length;

    useEffect(() => {
        if (drawerOpen) {
            document.body.classList.add('no-scroll');
        } else {
            document.body.classList.remove('no-scroll');
        }
        return () => document.body.classList.remove('no-scroll');
    }, [drawerOpen]);

    return (
        <>
            <nav style={styles.navbar} className="community-navbar">
                {/* Left Side - Company Logo */}
                <div className="navbar-logo-container">
                    <a href={window.location.origin}>
                        <img src={logo} style={styles.logo} className="navbar-logo" alt="Logo"/>
                    </a>
                </div>
                {/* Right Side - User Profile Image */}
                <Flex gap={12} align="center" justify="space-between" style={{marginRight:"16px"}}>
                    <Flex onClick={() => {
                        setDrawerOpen((prev) => !prev);
                    }} style={{ position: "relative" }}>
                        <PiBellRinging size={24} cursor={"pointer"} />
                        {unreadCount !== 0 && (
                            <span
                                style={{
                                    position: "absolute",
                                    top: 0,
                                    right: 0,
                                    width: 10,
                                    height: 10,
                                    background: "#22c55e",
                                    borderRadius: "50%",
                                    border: "2px solid #fafafa",
                                    display: "inline-block"
                                }}
                            />
                        )}
                    </Flex>
                    <button
                        onClick={handleProfileClick}
                        style={{
                            background: "none",
                            border: "none",
                            padding: 0,
                            cursor: "pointer"
                        }}
                        aria-label="Go to profile"
                    >
                        <img
                            src={userImage}
                            alt="Profile"
                            style={{
                                width: 40,
                                height: 40,
                                borderRadius: "50%",
                                objectFit: "cover",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.08)"
                            }}
                        />
                    </button>
                </Flex>
            </nav>
            <Drawer footer={<>
                <Flex gap={8} justify="space-between">
                    <Button type={"secondary"} onClick={() => setDrawerOpen(false)}>
                        Discard
                    </Button>
                    <Button type={"secondary"} onClick={() => handleMarkAsRead(null,true)}>
                        <Flex gap={4} align="center">
                            {notificationItemLoading === null && <FaSpinner className="spinner" />} Mark all as read
                        </Flex>
                    </Button>
                </Flex>
            </>} title={"Notifications"} open={drawerOpen} onClose={() => setDrawerOpen(false)}>
                {loading ? (
                    <Flex align={"center"} justify={"center"}><FaSpinner size={24} className="spinner" /></Flex>
                ) : notifications.length === 0 ? (
                    <p style={{marginLeft:"22px"}}>No notifications</p>
                ) : (
                    <div
                        ref={notificationsListRef}
                        style={{ maxHeight: 630, overflowX: "hidden" }}
                        onScroll={handleScroll}
                    >
                        { notifications.map((notif) => (
                            <Card hoverable={true} key={notif?.id} style={{margin: "8px"}} bordered={false}
                                  onClick={() => handleNotificationClick(notif)}>
                                <Flex gap={12} align="center">
                                    <Avatar src={notif.user_image} name={notif.user_name??"User"} style={{ opacity:"0.8"}} shape="circle" size={[46, 36]}/>
                                    <Flex direction={"column"} style={{width: '100%'}}>
                                        <Flex justify={"space-between"} alignItems={"center"} gap={6}>
                                            <strong>{notif.created_by_user_name}</strong>
                                            {/*{!notif.is_read && (*/}
                                            {/*    <button*/}
                                            {/*        style={{*/}
                                            {/*            marginLeft: 8,*/}
                                            {/*            background: "#ffffff",*/}
                                            {/*            color: "black",*/}
                                            {/*            border: "none",*/}
                                            {/*            borderRadius: "4px",*/}
                                            {/*            padding: "2px 8px",*/}
                                            {/*            fontSize: "12px",*/}
                                            {/*            cursor: "pointer"*/}
                                            {/*        }}*/}
                                            {/*        onClick={() => handleMarkAsRead(notif.id)}*/}
                                            {/*        aria-label="Mark as read"*/}
                                            {/*    >*/}
                                            {/*        <Flex gap={4} align="center">*/}
                                            {/*            {notificationItemLoading === notif.id ?*/}
                                            {/*                <FaSpinner size={8} className="spinner"/> :*/}
                                            {/*                <FaCircle size={8} color="#2563eb" title="Unread"/>}*/}
                                            {/*            Mark as read*/}
                                            {/*        </Flex>*/}
                                            {/*    </button>*/}
                                            {/*)}*/}
                                        </Flex>
                                        <div
                                            style={{
                                                // margin: "4px 0",
                                                fontSize: "14px",
                                                // color: "grey",
                                                lineHeight: '22px'
                                            }}
                                            dangerouslySetInnerHTML={{__html: truncateText(notif.message, 100)}}
                                        />
                                        <div style={{fontSize: "12px", color: "#888"}}>
                                            {formatTimeAgo(notif.created_at)}
                                        </div>
                                    </Flex>
                                    <Flex>
                                        {!notif.is_read && (
                                            <span
                                                style={{
                                                    position: "relative",
                                                    // top: 0,
                                                    // right: 0,
                                                    width: 10,
                                                    height: 10,
                                                    background: "rgb(151 135 255)",
                                                    borderRadius: "50%",
                                                    border: "2px solid #fafafa",
                                                    display: "inline-block"
                                                }}
                                            />
                                        )}
                                    </Flex>
                                </Flex>
                            </Card>
                        ))}
                        {loadingMore && (
                            <Flex align={"center"} justify={"center"} style={{ padding: 12 }}>
                                <FaSpinner size={18} className="spinner" />
                            </Flex>
                        )}
                        {!hasMore && (
                            <div style={{ textAlign: "center", color: "#888", padding: 8, fontSize: 12 }}>
                                No more notifications
                            </div>
                        )}
                    </div>
                )}
            </Drawer>
        </>
    );
};

const styles = {
    navbar: {
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "60px",
        backgroundColor: "#fafafa",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        boxShadow: "0px 2px 5px rgba(0,0,0,0.2)",
        zIndex: 1000,
    },
    logo: {
        height: "40px",
        padding: "10px",
    },
    notificationIcon: {
        background: "#bfbfbf",
        padding: "4px 8px",
        borderRadius: "8px",
        fontSize: "12px",
        fontWeight: 600,
        top: "12px",
        position: "absolute",
        cursor:"pointer",
        right: "58px",
        display:"flex",
        alignItems:"center",
        justifyContent:"center"
    }
};

export default Navbar;